import React, { useState } from 'react';

const MdmExplained = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const mdmFeatures = [
    {
      icon: "fas fa-shield-alt",
      title: "Keamanan Terpusat",
      description: "Mengontrol dan mengamankan semua perangkat dari satu dashboard",
      example: "Guru dapat memblokir akses media sosial selama jam pelajaran"
    },
    {
      icon: "fas fa-download",
      title: "Instalasi Aplikasi",
      description: "Memasang atau menghapus aplikasi secara remote",
      example: "Otomatis menginstal aplikasi Al-Qur'an digital ke semua iPad santri"
    },
    {
      icon: "fas fa-clock",
      title: "Kontrol Waktu",
      description: "Mengatur jadwal penggunaan perangkat",
      example: "iPad otomatis terkunci saat waktu sholat dan tidur malam"
    },
    {
      icon: "fas fa-eye",
      title: "Monitoring Real-time",
      description: "Memantau aktivitas penggunaan perangkat",
      example: "Melihat aplikasi apa saja yang digunakan santri hari ini"
    },
    {
      icon: "fas fa-wifi",
      title: "Kontrol Internet",
      description: "Membatasi akses website dan konten",
      example: "Memblokir situs hiburan, hanya mengizinkan situs edukasi"
    },
    {
      icon: "fas fa-cog",
      title: "Konfigurasi Remote",
      description: "Mengatur pengaturan perangkat dari jarak jauh",
      example: "Mengatur brightness dan volume iPad secara seragam"
    }
  ];

  const useCases = [
    {
      scenario: "Saat Pembelajaran",
      icon: "fas fa-chalkboard-teacher",
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50",
      controls: [
        "Membuka aplikasi pembelajaran yang diperlukan",
        "Menutup aplikasi game dan hiburan",
        "Mengatur iPad dalam mode 'Fokus Belajar'",
        "Membagikan materi pelajaran ke semua iPad"
      ]
    },
    {
      scenario: "Saat Waktu Istirahat",
      icon: "fas fa-coffee",
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-50",
      controls: [
        "Mengizinkan akses aplikasi tertentu untuk relaksasi",
        "Tetap memblokir konten tidak pantas",
        "Membatasi waktu penggunaan aplikasi hiburan",
        "Monitoring aktivitas santri"
      ]
    },
    {
      scenario: "Saat Waktu Sholat",
      icon: "fas fa-mosque",
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50",
      controls: [
        "Mengunci semua iPad secara otomatis",
        "Menampilkan reminder waktu sholat",
        "Membuka akses aplikasi Qibla dan adzan",
        "Menonaktifkan notifikasi yang mengganggu"
      ]
    },
    {
      scenario: "Saat Waktu Tidur",
      icon: "fas fa-bed",
      color: "from-indigo-500 to-indigo-600",
      bgColor: "bg-indigo-50",
      controls: [
        "Mengunci iPad sepenuhnya dari jam 22:00",
        "Mengaktifkan mode 'Do Not Disturb'",
        "Mematikan akses internet",
        "Hanya mengizinkan aplikasi alarm"
      ]
    }
  ];

  const beforeAfter = {
    before: {
      title: "Sebelum Menggunakan MDM",
      icon: "fas fa-times-circle",
      color: "text-red-600",
      bgColor: "bg-red-50",
      problems: [
        "Santri bermain game saat pelajaran",
        "Akses media sosial tidak terkontrol",
        "Sulit memantau aktivitas digital santri",
        "iPad hilang atau rusak sulit dilacak",
        "Instalasi aplikasi tidak seragam",
        "Wali santri khawatir dengan penggunaan iPad"
      ]
    },
    after: {
      title: "Setelah Menggunakan MDM",
      icon: "fas fa-check-circle",
      color: "text-green-600",
      bgColor: "bg-green-50",
      solutions: [
        "iPad otomatis terkunci saat jam pelajaran",
        "Akses internet difilter sesuai kebutuhan",
        "Laporan aktivitas real-time untuk guru dan wali",
        "Pelacakan lokasi iPad yang hilang",
        "Aplikasi terinstal otomatis dan seragam",
        "Wali santri tenang dengan kontrol yang ketat"
      ]
    }
  };

  const educationBenefits = [
    {
      stakeholder: "Untuk Santri",
      icon: "fas fa-user-graduate",
      color: "from-blue-500 to-blue-600",
      benefits: [
        "Fokus belajar tanpa gangguan aplikasi hiburan",
        "Akses konten edukatif yang aman dan terpilih",
        "Jadwal penggunaan yang teratur dan sehat",
        "Perlindungan dari konten negatif internet"
      ]
    },
    {
      stakeholder: "Untuk Guru",
      icon: "fas fa-chalkboard-teacher",
      color: "from-green-500 to-green-600",
      benefits: [
        "Kontrol penuh atas iPad santri saat mengajar",
        "Distribusi materi pembelajaran yang mudah",
        "Monitoring aktivitas belajar santri",
        "Manajemen kelas digital yang efektif"
      ]
    },
    {
      stakeholder: "Untuk Wali Santri",
      icon: "fas fa-users",
      color: "from-purple-500 to-purple-600",
      benefits: [
        "Laporan aktivitas digital anak secara real-time",
        "Ketenangan pikiran dengan kontrol yang ketat",
        "Transparansi penggunaan iPad di sekolah",
        "Komunikasi yang lebih baik dengan sekolah"
      ]
    },
    {
      stakeholder: "Untuk Sekolah",
      icon: "fas fa-school",
      color: "from-orange-500 to-orange-600",
      benefits: [
        "Manajemen ratusan iPad dari satu dashboard",
        "Keamanan data dan perangkat terjamin",
        "Efisiensi operasional yang tinggi",
        "Reputasi sekolah yang lebih baik"
      ]
    }
  ];

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full mb-6">
            <i className="fas fa-mobile-alt text-white text-3xl"></i>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Apa itu MDM?
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Mobile Device Management - Solusi Cerdas untuk Mengelola iPad di Lingkungan Pendidikan
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center mb-8 bg-white rounded-xl shadow-lg p-2">
          {[
            { id: 'overview', label: 'Penjelasan MDM', icon: 'fas fa-info-circle' },
            { id: 'features', label: 'Fitur Utama', icon: 'fas fa-star' },
            { id: 'usecases', label: 'Contoh Penggunaan', icon: 'fas fa-play-circle' },
            { id: 'benefits', label: 'Manfaat', icon: 'fas fa-thumbs-up' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                  : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
              }`}
            >
              <i className={tab.icon}></i>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-12">
            {/* What is MDM */}
            <section className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                <i className="fas fa-question-circle text-blue-500 mr-3"></i>
                Apa itu MDM?
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 mb-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Definisi Sederhana:</h3>
                    <p className="text-gray-700 text-lg leading-relaxed">
                      MDM adalah seperti <strong>"remote control"</strong> untuk iPad. Bayangkan Anda bisa mengontrol ratusan iPad santri dari satu tempat, seperti mengontrol TV dengan remote control.
                    </p>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="fas fa-check text-white text-sm"></i>
                      </div>
                      <p className="text-gray-700">Mengontrol aplikasi apa saja yang bisa digunakan</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="fas fa-check text-white text-sm"></i>
                      </div>
                      <p className="text-gray-700">Mengatur waktu penggunaan iPad</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="fas fa-check text-white text-sm"></i>
                      </div>
                      <p className="text-gray-700">Memantau aktivitas santri secara real-time</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="fas fa-check text-white text-sm"></i>
                      </div>
                      <p className="text-gray-700">Melindungi dari konten yang tidak pantas</p>
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl p-8">
                    <div className="text-6xl mb-4">📱</div>
                    <div className="text-2xl mb-2">+</div>
                    <div className="text-6xl mb-4">🎮</div>
                    <div className="text-2xl mb-2">=</div>
                    <div className="text-6xl">🛡️</div>
                    <p className="text-gray-700 mt-4 font-medium">iPad + Kontrol = Pembelajaran Aman</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Before vs After */}
            <section>
              <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
                Perbandingan Sebelum dan Sesudah MDM
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Before */}
                <div className={`${beforeAfter.before.bgColor} rounded-2xl p-8`}>
                  <div className="text-center mb-6">
                    <i className={`${beforeAfter.before.icon} ${beforeAfter.before.color} text-4xl mb-4`}></i>
                    <h3 className="text-2xl font-bold text-gray-800">{beforeAfter.before.title}</h3>
                  </div>
                  <div className="space-y-4">
                    {beforeAfter.before.problems.map((problem, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <i className="fas fa-exclamation-triangle text-red-500 mt-1"></i>
                        <p className="text-gray-700">{problem}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* After */}
                <div className={`${beforeAfter.after.bgColor} rounded-2xl p-8`}>
                  <div className="text-center mb-6">
                    <i className={`${beforeAfter.after.icon} ${beforeAfter.after.color} text-4xl mb-4`}></i>
                    <h3 className="text-2xl font-bold text-gray-800">{beforeAfter.after.title}</h3>
                  </div>
                  <div className="space-y-4">
                    {beforeAfter.after.solutions.map((solution, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <i className="fas fa-check-circle text-green-500 mt-1"></i>
                        <p className="text-gray-700">{solution}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* Features Tab */}
        {activeTab === 'features' && (
          <section>
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
              Fitur Utama MDM
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mdmFeatures.map((feature, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${feature.icon} text-white text-2xl`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 mb-4">{feature.description}</p>
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      <i className="fas fa-lightbulb text-blue-600 mr-2"></i>
                      <strong>Contoh:</strong> {feature.example}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Use Cases Tab */}
        {activeTab === 'usecases' && (
          <section>
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
              Contoh Penggunaan MDM di Pesantren
            </h2>
            <div className="space-y-8">
              {useCases.map((useCase, index) => (
                <div key={index} className={`${useCase.bgColor} rounded-2xl shadow-lg overflow-hidden`}>
                  <div className={`h-2 bg-gradient-to-r ${useCase.color}`}></div>
                  <div className="p-8">
                    <div className="flex items-center mb-6">
                      <div className={`w-16 h-16 bg-gradient-to-r ${useCase.color} rounded-full flex items-center justify-center mr-4`}>
                        <i className={`${useCase.icon} text-white text-2xl`}></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-800">{useCase.scenario}</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {useCase.controls.map((control, controlIndex) => (
                        <div key={controlIndex} className="bg-white rounded-lg p-4 shadow-sm">
                          <div className="flex items-start space-x-3">
                            <i className="fas fa-cog text-blue-500 mt-1"></i>
                            <p className="text-gray-700">{control}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Benefits Tab */}
        {activeTab === 'benefits' && (
          <section>
            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
              Manfaat MDM untuk Semua Pihak
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {educationBenefits.map((benefit, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center mb-6">
                    <div className={`w-16 h-16 bg-gradient-to-r ${benefit.color} rounded-full flex items-center justify-center mr-4`}>
                      <i className={`${benefit.icon} text-white text-2xl`}></i>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-800">{benefit.stakeholder}</h3>
                  </div>
                  <div className="space-y-4">
                    {benefit.benefits.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex items-start space-x-3">
                        <i className="fas fa-check-circle text-green-500 mt-1"></i>
                        <p className="text-gray-700">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Call to Action */}
        <section className="mt-16">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white bg-opacity-20 rounded-full mb-6">
              <i className="fas fa-rocket text-white text-2xl"></i>
            </div>
            <h2 className="text-3xl font-bold mb-4">Siap Mengimplementasikan MDM?</h2>
            <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
              MDM adalah kunci sukses digitalisasi pendidikan yang aman, terkontrol, dan efektif untuk semua stakeholder
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-blue-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                <i className="fas fa-play mr-2"></i>
                Mulai Implementasi
              </button>
              <button className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                <i className="fas fa-question-circle mr-2"></i>
                Konsultasi Gratis
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default MdmExplained;
